import { useState } from 'react';

import { useDispatch } from 'react-redux';

// admin route
import { verifyIdDocument as verifyIdDocumentThunk } from 'api/candidate-application-slice';
import { useVerifyIdDocumentMutation } from 'api/candidates-api';

// token route

export const buildIdvPayload = ({
  uploadId,
  idCode,
  idType,
  idCountry = null,
  expiryTolerance = null,
  listCode = null,
  backUploadId = null,
  skipVerification = false,
}) => ({
  upload_id: uploadId,
  selected_id_code: idCode,
  selected_id_type: idType,
  selected_id_country: idCountry ? String(idCountry).toUpperCase() : null,
  expiry_tolerance: expiryTolerance ?? null,
  listCode: listCode ?? null,
  back_upload_id: backUploadId ?? null,
  skip_verification: skipVerification ? 1 : 0,
});

export function useVerifyIdDocument(ctx = {}, opts = {}) {
  const dispatch = useDispatch();

  // Admin path (RTK Query)
  const [mutate, { isLoading: adminLoading, error: adminError }] = useVerifyIdDocumentMutation();

  // Token path (thunk)
  const [thunkLoading, setThunkLoading] = useState(false);
  const [thunkError, setThunkError] = useState(null);

  const verify = async ({
    uploadId,
    idCode,
    idType,
    idCountry = null,
    expiryTolerance = null,
    listCode = null,
    backUploadId = null,
    skipVerification = false,
  }) => {
    const data = buildIdvPayload({
      uploadId,
      idCode,
      idType,
      idCountry,
      expiryTolerance,
      listCode,
      backUploadId,
      skipVerification,
    });

    const runOnSuccess = async res => {
      try {
        const after = opts.onSuccess?.(res);
        if (after != null && typeof after.then === 'function') {
          await after;
        }
      } catch (e) {
        console.error('useVerifyIdDocument onSuccess', e);
        throw e;
      }
    };

    if (ctx.token) {
      setThunkLoading(true);
      setThunkError(null);
      let res;
      try {
        res = await dispatch(verifyIdDocumentThunk({ token: ctx.token, data })).unwrap();
      } catch (e) {
        setThunkError(e);
        setThunkLoading(false);
        throw e;
      }
      try {
        await runOnSuccess(res);
      } finally {
        setThunkLoading(false);
      }
      return res;
    }

    if (ctx.candidateId) {
      const res = await mutate({ id: ctx.candidateId, data }).unwrap();
      await runOnSuccess(res);
      return res;
    }

    throw new Error('useVerifyIdDocument: pass either token or candidateId');
  };

  const isVerifying = ctx.token ? thunkLoading : adminLoading;
  const error = ctx.token ? thunkError : adminError;

  return { verify, isVerifying, error };
}

export const useVerifyIdDocumentByToken = (token, opts) => useVerifyIdDocument({ token }, opts);

export const useVerifyIdDocumentAdmin = (candidateId, opts) =>
  useVerifyIdDocument({ candidateId }, opts);
